import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Checkbox, FormControlLabel, Grid, Tooltip } from "@mui/material";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import DateField from "Components/Common/DatePicker";
import { getOptionValue } from "common/enums";

import { onlyNumberKeys } from "utils";

import { useGetSourceOfPowersQuery } from "apis/TechnicalSanction";
import { applicationTypes } from "common/constant";

const UtilityConsumerDetails = ({ formik, isPowerUtilityConsumer, t }) => {
  const {
    values: {
      isIGSTExempted,
      isApplicationTempBypassEnabled,
      utilities,
      applicationType,
    },
    setFieldValue,
  } = formik;

  const { data: { data: sourceOfPowerOptions } = {} } =
    useGetSourceOfPowersQuery();

  const isSolidWasteUtilityConsumer = useMemo(
    () =>
      utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "Solid Waste")
      ),
    [utilities]
  );

  const isDcsUtilityConsumer = useMemo(
    () =>
      utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "DCS")
      ),
    [utilities]
  );

  return (
    <Grid container columnSpacing={1}>
      <Grid item xs={12} sm={4}>
        <InputFieldText
          label={t("Consumer Number") + "*"}
          name="consumerNumber"
          formik={formik}
          InputProps={{
            inputProps: { min: 0, onKeyDown: (e) => onlyNumberKeys(e) },
          }}
          placeholder="Enter Consumer Number"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputFieldText
          label={t("Consumer Name") + "*"}
          name="consumerName"
          formik={formik}
          placeholder="Enter Consumer Name"
        />
      </Grid>
      {isPowerUtilityConsumer && (
        <>
          <Grid item xs={12} sm={4}>
            <SelectField
              label={t("Source Of Power") + "*"}
              name="sourceOfPowerId"
              formik={formik}
              options={sourceOfPowerOptions || []}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ id }) => id}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Electricity Duty") + "*"}
              name="electricityDuty"
              type="number"
              formik={formik}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Electricity Duty"
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={4}>
        <InputFieldText
          label={
            t(
              isPowerUtilityConsumer &&
                applicationTypes["Temporary Connection"] === applicationType
                ? "Temporary Connection Deposit"
                : "Security Deposit"
            ) + "*"
          }
          name="securityDeposit"
          type="number"
          formik={formik}
          InputProps={{
            inputProps: {
              min: 0,
              onKeyDown: (e) => onlyNumberKeys(e, true),
            },
          }}
          placeholder={
            isPowerUtilityConsumer &&
            applicationTypes["Temporary Connection"] === applicationType
              ? "Temporary Connection Deposit"
              : "Security Deposit"
          }
        />
      </Grid>
      {isPowerUtilityConsumer && (
        <Grid item xs={12} sm={4}>
          <InputFieldText
            label={t("Service Connection Charges*")}
            name="serviceConnectionCharges"
            type="number"
            formik={formik}
            placeholder="Service Connection Charges"
          />
        </Grid>
      )}
      {!isPowerUtilityConsumer && (
        <Grid item xs={12} sm={4}>
          <InputFieldText
            formik={formik}
            name="salesOrder"
            label={t("Sales Order") + "*"}
            placeholder={t("Enter Sales Order")}
          />
        </Grid>
      )}
      {isSolidWasteUtilityConsumer && (
        <Grid item xs={12} sm={4}>
          <InputFieldText
            formik={formik}
            name="serialNumber"
            label={t("Serial Number") + "*"}
            placeholder={t("Enter Serial Number")}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <DateField
          label={t("Connection Date")}
          name="dateOfConnection"
          formik={formik}
          format="DD-MM-YYYY"
          placeholder="Select Connection Date"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DateField
          label={t("Disconnection Date")}
          name="disconnectionDate"
          formik={formik}
          format="DD-MM-YYYY"
          placeholder="Select Disconnection Date"
        />
      </Grid>
      {isPowerUtilityConsumer && (
        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!isIGSTExempted}
                onChange={() =>
                  setFieldValue("isIGSTExempted", !isIGSTExempted)
                }
              />
            }
            label={t("IGST Exempted")}
          />
        </Grid>
      )}
      {isDcsUtilityConsumer &&
        applicationTypes["Temporary Connection"] === applicationType && (
          <Grid item xs={12} sm={4}>
            <Tooltip
              arrow
              title={t(
                "This will bypass the status from \"Approved\" directly to \"Process Complete\"."
              )}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!isApplicationTempBypassEnabled}
                    onChange={() =>
                      setFieldValue(
                        "isApplicationTempBypassEnabled",
                        !isApplicationTempBypassEnabled
                      )
                    }
                  />
                }
                label={t("Application Temporary Bypass Enabled")}
              />
            </Tooltip>
          </Grid>
        )}
    </Grid>
  );
};

UtilityConsumerDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
  isPowerUtilityConsumer: PropTypes.bool,
};

export default withTranslation()(UtilityConsumerDetails);
